import React, { useState } from "react";
import { FaDownload, FaEdit, FaTrashAlt } from "react-icons/fa";
import Modal from "../../components/medicine-schedules/Modal";

const MedicineScheduleList = ({
  taggedMedinceData,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  // State to control deletion confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);

  const handleDownload = () => {
    const csvHeader = [
      "id,generic_name,drug_name,schedule,is_prescription_required",
    ];
    const csvRows = taggedMedinceData?.length
      ? taggedMedinceData?.map((medicine) =>
          [
            medicine.id,
            medicine.generic_name,
            medicine.drug_name,
            medicine.schedule,
            medicine.is_prescription_required,
          ].join(",")
        )
      : [];

    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medicine_schedule.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // When the delete button is clicked, open the confirmation modal.
  const handleDeleteClick = (medicine) => {
    setMedicineToDelete(medicine);
    setDeleteModalOpen(true);
  };

  // Confirm deletion: call the deletion callback, then close the modal.
  const confirmDelete = () => {
    if (medicineToDelete) {
      onDelete(medicineToDelete.id);
      setDeleteModalOpen(false);
      setMedicineToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setMedicineToDelete(null);
  };

  return (
    <div className="overflow-x-auto bg-gray-50 rounded-lg  p-2">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800"></h2>
        <button
          onClick={handleDownload}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md flex items-center gap-2 hover:bg-blue-700 transition-all"
        >
          <FaDownload size={16} />
          Download CSV
        </button>
      </div>

      {taggedMedinceData?.length === 0 ? (
        <div className="text-gray-500 text-center py-10">
          <p className="mb-2 text-lg font-semibold">No Medicines Available</p>
          <p className="mb-2">
            To begin, click the <strong>Add Medicine</strong> button to manually
            add a new medicine.
          </p>
          <p>
            Alternatively, you can use the <strong>Download CSV</strong> button
            at the top right to download a sample CSV format. Populate the
            template with your medicine data and then use the bulk upload
            feature to add multiple medicines at once.
          </p>
        </div>
      ) : (
        <table className="min-w-full border-collapse">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="py-2 px-2 text-left text-sm font-semibold uppercase tracking-wider">
                S.No
              </th>
              <th className="py-2 px-3 text-left text-sm font-semibold uppercase tracking-wider">
                Drug Name
              </th>
              <th className="py-2 px-3 text-left text-sm font-semibold uppercase tracking-wider">
                Generic Name
              </th>
              <th className="py-2 px-3 text-left text-sm font-semibold uppercase tracking-wider">
                Schedule
              </th>
              <th className="py-2 px-3 text-left text-sm font-semibold uppercase tracking-wider">
                Prescription Required
              </th>
              <th className="py-2 px-3 text-left text-sm font-semibold uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {taggedMedinceData?.map((medicine, index) => (
              <tr
                key={medicine.id}
                className={`${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                } hover:bg-blue-50 transition-all`}
              >
                <td className="py-2 px-3 text-sm text-gray-700">{index + 1}</td>
                <td className="py-2 px-3 text-sm text-gray-700">
                  {medicine.drug_name}
                </td>
                <td className="py-2 px-3 text-sm text-gray-700">
                  {medicine.generic_name}
                </td>
                <td className="py-2 px-3 text-sm text-gray-700">
                  {medicine.schedule}
                </td>
                <td className="py-2 px-3 text-sm">
                  <span
                    className={`px-3 py-1 text-white text-xs font-semibold rounded-full ${
                      medicine.is_prescription_required
                        ? "bg-green-500"
                        : "bg-gray-500"
                    }`}
                  >
                    {medicine.is_prescription_required ? "Yes" : "No"}
                  </span>
                </td>
                <td className="py-2 px-3 text-sm flex gap-4">
                  <button
                    onClick={() => onEdit(medicine)}
                    className="text-blue-500 hover:text-blue-700"
                    title="Edit"
                  >
                    <FaEdit size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(medicine)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FaTrashAlt size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteModalOpen && (
        <Modal isOpen={deleteModalOpen} onClose={cancelDelete}>
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Confirm Deletion
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete{" "}
              <strong>{medicineToDelete?.drug_name}</strong>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MedicineScheduleList;
